import type { IQuote, IQuoteIndex, IQuoteSearchParams, IQuoteState, IQuoteStateTransition } from '@/interfaces'
import { Quote, QuoteIndex, QuoteSearchParams, QuoteState, QuoteStateTransition } from '@/models'
import { BaseService, type IAuthService } from '@/services'
import { APIEndpoints, APIScopes, APIURL } from '@/services/API'

export class QuoteService extends BaseService {
	constructor(authService: IAuthService) {
		super(APIURL, authService, APIScopes)
	}

	/**
	 * Get all quotes for a specific subjob
	 * Uses optimized QuoteIndex model for better list performance
	 *
	 * @param subjobId - The subjob ID to get quotes for
	 * @returns Array of QuoteIndex models with summary data
	 */
	async getQuotesBySubJob(subjobId: number): Promise<IQuoteIndex[]> {
		return this.getData<IQuoteIndex[]>(`${APIEndpoints.quote}/by-subjob/${subjobId}`)
			.then((data) => {
				// Map to QuoteIndex models for proper date parsing and methods
				return data.map((q: IQuoteIndex) => new QuoteIndex(q))
			})
			.catch((err) => {
				// Log error for debugging
				console.error('Failed to fetch quotes by subjob:', err)
				throw err
			})
	}

	/**
	 * Search quotes with optional filtering parameters
	 * Provides flexible querying with Row Level Security support
	 *
	 * @param params - Search parameters for filtering quotes
	 * @returns Array of QuoteIndex models matching the search criteria
	 */
	async searchQuotes(params: IQuoteSearchParams = {}): Promise<IQuoteIndex[]> {
		// Create a QuoteSearchParams instance to handle parameter conversion
		const searchParams = new QuoteSearchParams(params)
		const queryParams = searchParams.toApiParams()

		// Build query string from parameters
		const queryString = new URLSearchParams(queryParams as Record<string, string>).toString()
		const endpoint = queryString ? `${APIEndpoints.quote}/search?${queryString}` : `${APIEndpoints.quote}/search`

		return this.getData<IQuoteIndex[]>(endpoint)
			.then((data) => {
				// Map to QuoteIndex models for proper date parsing and methods
				return data.map((q: IQuoteIndex) => new QuoteIndex(q))
			})
			.catch((err) => {
				// Log error for debugging
				console.error('Failed to search quotes:', err)
				throw err
			})
	}

	async getQuote(quoteId: number | string): Promise<IQuote> {
		return this.getData<IQuote>(`${APIEndpoints.quote}/${quoteId}`)
			.then((data) => {
				return new Quote(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async createQuote(quote: Partial<IQuote>): Promise<IQuote> {
		return this.postData<Partial<IQuote>, IQuote>(`${APIEndpoints.quote}/add`, quote)
			.then((data) => {
				return new Quote(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async updateQuote(quote: IQuote): Promise<IQuote> {
		return this.patchData<IQuote>(`${APIEndpoints.quote}/update/${quote.quoteId}`, quote)
			.then((data) => {
				return new Quote(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async deleteQuote(quoteId: number | string): Promise<void> {
		return this.deleteData<void>(`${APIEndpoints.quote}/${quoteId}`).catch((err) => {
			throw err
		})
	}

	async transitionState(quoteId: number | string, newState: number): Promise<IQuote> {
		// Use the correct endpoint format: /quotes/{quote_id}/state/{state}
		return this.postData<Record<string, never>, IQuote>(`${APIEndpoints.quote}/${quoteId}/state/${newState}`, {})
			.then((data) => {
				return new Quote(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async getQuoteStates(): Promise<IQuoteState[]> {
		return this.getData<IQuoteState[]>(`${APIEndpoints.quote}/states`)
			.then((data) => {
				return data.map((s: IQuoteState) => new QuoteState(s))
			})
			.catch((err) => {
				throw err
			})
	}

	async getQuoteStateTransitions(): Promise<IQuoteStateTransition[]> {
		return this.getData<IQuoteStateTransition[]>(`${APIEndpoints.quote}/state-transitions`)
			.then((data) => {
				return data.map((t: IQuoteStateTransition) => new QuoteStateTransition(t))
			})
			.catch((err) => {
				throw err
			})
	}

	/**
	 * Close a quote permanently
	 * @param quoteId - The ID of the quote to close
	 * @returns Success response with status and message
	 */
	async closeQuote(quoteId: number | string): Promise<{ status: string; message: string }> {
		return this.postData<Record<string, never>, { status: string; message: string }>(
			`${APIEndpoints.quote}/${quoteId}/close`,
			{}
		).catch((err) => {
			throw err
		})
	}

	/**
	 * Cancel a quote permanently (soft delete)
	 * @param quoteId - The ID of the quote to cancel
	 * @returns Success response with status and message
	 */
	async cancelQuote(quoteId: number | string): Promise<{ status: string; message: string }> {
		return this.postData<Record<string, never>, { status: string; message: string }>(
			`${APIEndpoints.quote}/${quoteId}/cancel`,
			{}
		).catch((err) => {
			throw err
		})
	}
}
